class ComparisonTable extends HTMLElement {
  constructor() {
    super();
    this._isDragging = false;
    this._touchStarted = false;
    this._onScroll = this._updateScrollbar.bind(this);
    this._onResize = this._handleResize.bind(this);
    this._onMouseDown = this._handleMouseDown.bind(this);
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseUp = this._handleMouseUp.bind(this);
    this._onTouchStart = this._handleTouchStart.bind(this);
    this._onTouchMove = this._handleTouchMove.bind(this);
    this._onTouchEnd = this._handleTouchEnd.bind(this);
  }

  connectedCallback() {
    this.slider = this.querySelector('.comparison-table__slider');
    this.progressContainer = this.querySelector('.comparison-table__progress-container');
    this.progressBar = this.querySelector('.comparison-table__progress-bar');

    if (!this.slider || !this.progressContainer || !this.progressBar) return;

    this.slider.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    this.progressContainer.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);

    this.progressContainer.addEventListener('touchstart', this._onTouchStart, { passive: false });
    document.addEventListener('touchmove', this._onTouchMove, { passive: false });
    document.addEventListener('touchend', this._onTouchEnd);

    [100, 300, 500, 1000].forEach((delay) =>
      setTimeout(() => {
        this._syncRowHeights();
        this._updateScrollbar();
      }, delay)
    );
  }

  _syncRowHeights() {
    if (!this.slider) return;
    const cards = Array.from(this.slider.querySelectorAll('.comparison-table__card'));
    if (cards.length < 2) return;

    const rowsByCard = cards.map((card) =>
      Array.from(card.querySelectorAll('.comparison-table__card-row'))
    );
    rowsByCard.forEach((rows) => rows.forEach((row) => (row.style.minHeight = '')));

    const rowCount = Math.max(...rowsByCard.map((rows) => rows.length));
    for (let i = 0; i < rowCount; i++) {
      const rowsAtIndex = rowsByCard.map((rows) => rows[i]).filter(Boolean);
      if (rowsAtIndex.length < 2) continue;
      const max = Math.max(...rowsAtIndex.map((row) => row.offsetHeight));
      rowsAtIndex.forEach((row) => (row.style.minHeight = `${max}px`));
    }
  }

  _handleResize() {
    this._syncRowHeights();
    this._updateScrollbar();
  }

  disconnectedCallback() {
    if (this.slider) this.slider.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this.progressContainer) this.progressContainer.removeEventListener('mousedown', this._onMouseDown);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    if (this.progressContainer) this.progressContainer.removeEventListener('touchstart', this._onTouchStart);
    document.removeEventListener('touchmove', this._onTouchMove);
    document.removeEventListener('touchend', this._onTouchEnd);
  }

  _updateScrollbar() {
    if (!this.slider || !this.progressBar) return;
    const sliderWidth = this.slider.clientWidth;
    const scrollWidth = this.slider.scrollWidth;
    const maxScrollLeft = scrollWidth - sliderWidth;

    if (maxScrollLeft <= 0) {
      this.progressBar.style.width = '100%';
      this.progressBar.style.left = '0';
      return;
    }

    const thumbWidthPercent = (sliderWidth / scrollWidth) * 100;
    const scrollRatio = this.slider.scrollLeft / maxScrollLeft;
    const maxThumbTravel = 100 - thumbWidthPercent;
    const thumbLeftPercent = scrollRatio * maxThumbTravel;

    this.progressBar.style.width = `${thumbWidthPercent}%`;
    this.progressBar.style.left = `${thumbLeftPercent}%`;
  }

  _scrollToPosition(clientX) {
    if (!this.slider || !this.progressContainer) return;
    const rect = this.progressContainer.getBoundingClientRect();
    const relativeX = clientX - rect.left;

    const sliderWidth = this.slider.clientWidth;
    const scrollWidth = this.slider.scrollWidth;
    const maxScrollLeft = scrollWidth - sliderWidth;

    if (maxScrollLeft <= 0) return;

    const thumbWidthPx = (sliderWidth / scrollWidth) * rect.width;
    const thumbLeftPx = relativeX - thumbWidthPx / 2;
    const maxThumbLeftPx = rect.width - thumbWidthPx;
    let scrollRatio = thumbLeftPx / maxThumbLeftPx;
    scrollRatio = Math.max(0, Math.min(1, scrollRatio));

    this.slider.scrollLeft = scrollRatio * maxScrollLeft;
  }

  _handleMouseDown(event) {
    this._isDragging = true;
    this.progressContainer.classList.add('is-dragging');
    this._scrollToPosition(event.clientX);
    event.preventDefault();
  }

  _handleMouseMove(event) {
    if (!this._isDragging) return;
    this._scrollToPosition(event.clientX);
    event.preventDefault();
  }

  _handleMouseUp() {
    if (!this._isDragging) return;
    this._isDragging = false;
    this.progressContainer.classList.remove('is-dragging');
  }

  _handleTouchStart(event) {
    this._touchStarted = true;
    this._isDragging = true;
    this.progressContainer.classList.add('is-dragging');
    if (event.touches.length > 0) {
      this._scrollToPosition(event.touches[0].clientX);
    }
    event.preventDefault();
  }

  _handleTouchMove(event) {
    if (!this._isDragging || !this._touchStarted) return;
    if (event.touches.length > 0) {
      this._scrollToPosition(event.touches[0].clientX);
      event.preventDefault();
    }
  }

  _handleTouchEnd() {
    if (!this._touchStarted) return;
    this._isDragging = false;
    this._touchStarted = false;
    this.progressContainer.classList.remove('is-dragging');
  }
}

if (!customElements.get('comparison-table')) {
  customElements.define('comparison-table', ComparisonTable);
}
